/* eslint-disable react/display-name */
import PropTypes from "prop-types";
import { forwardRef } from "react";
import { ExclamationCircleIcon } from "@heroicons/react/solid";

const TextField = forwardRef(
  (
    {
      name,
      inputProps,
      labelProps,
      id,
      label,
      placeholder,
      helpText,
      error,
      value,
      onChange,
      required,
      disabled,
    },
    ref,
  ) => {
    const { endAdornment, ...restOfInputProps } = inputProps;
    const hasError = Boolean(error);
    const hasLabel = Boolean(label);
    const hasAdornment = Boolean(endAdornment);
    const hasHelpText = Boolean(helpText);
    return (
      <div>
        <label
          className={`
          ${hasLabel ? "mb-xs block gn-text-sm text-gray-900" : "sr-only"}
        `}
          {...labelProps}
        >
          {label}
        </label>
        <div className="relative">
          <input
            ref={ref}
            name={name}
            id={id}
            className={`block w-full h-input pl-sm gn-text-md rounded border-sm border-gray-300 shadow-lightSM
            ${
              hasError
                ? "text-danger placeholder-danger border-none ring ring-danger focus:ring focus:ring-danger"
                : "text-gray-900 placeholder-gray-500 focus:ring focus:ring-purple focus:outline-none focus:border-none"
            }
           `}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={required}
            {...restOfInputProps}
          />
          {hasAdornment ? (
            <div
              className="absolute inset-y-0 right-md flex items-center"
              data-testid="end-adornment"
            >
              {endAdornment}
            </div>
          ) : null}
        </div>
        {hasHelpText ? <TextField.HelpText helpText={helpText} /> : null}
        {hasError ? (
          <div
            className="mt-xs flex flex-row gap-xs"
            data-testid="error-text-test"
          >
            <ExclamationCircleIcon
              className="h-input-icon text-danger"
              aria-hidden="true"
            />
            <div className="gn-text-sm text-danger">{error}</div>
          </div>
        ) : null}
      </div>
    );
  },
);

function HelpText({ helpText }) {
  return (
    <div className="mt-xs gn-text-sm" data-testid="helptext-test">
      {helpText}
    </div>
  );
}

TextField.HelpText = HelpText;

HelpText.propTypes = {
  helpText: PropTypes.string,
};

HelpText.defaultProps = {
  helpText: undefined,
};

TextField.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helpText: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  inputProps: PropTypes.shape({
    type: PropTypes.string,
    endAdornment: PropTypes.element,
    onKeyUp: PropTypes.func,
    onBlur: PropTypes.func,
  }),
  labelProps: PropTypes.shape({}),
};

TextField.defaultProps = {
  id: undefined,
  name: undefined,
  label: undefined,
  placeholder: undefined,
  error: undefined,
  value: "",
  helpText: undefined,
  required: undefined,
  disabled: undefined,
  inputProps: {
    type: "text",
    endAdornment: undefined,
    onKeyUp: () => {},
    onBlur: () => {},
  },
  labelProps: {},
};

export default TextField;
